/**
 * The icon set, as one component.
 *
 * Every glyph is drawn from the approved design's own paths rather than pulled from an icon
 * library. Two reasons, and the second is the one that matters:
 *
 *   1. A library would be a dependency, a bundle cost and a licence to check, for something
 *      this product uses thirty of.
 *   2. The design *is* the specification. Copying its paths means the rail in the built
 *      product is the rail in the prototype — not a similar icon from a similar set that
 *      quietly makes every screen a bit different from the thing that was approved.
 *
 * Icons are decoration wherever a text label sits beside them, so they carry
 * `aria-hidden`. Where one stands alone — the rail collapsed, an icon-only button — the
 * *button* carries the accessible name. An icon is never the only thing telling someone what
 * a control does.
 */

const PATHS = {
  menu: 'M3 6h18M3 12h18M3 18h18',
  plus: 'M12 5v14M5 12h14',
  chevronDown: 'm6 9 6 6 6-6',
  // Direction of travel. Three shapes, so ahead/level/behind survive greyscale, a
  // projector, and the roughly one man in twelve who cannot separate the amber from the red.
  arrowUp: 'M12 19V5M5 12l7-7 7 7',
  arrowDown: 'M12 5v14M19 12l-7 7-7-7',
  level: 'M5 12h14',
  chevronEnd: 'm9 18 6-6-6-6',
  check: 'M20 6 9 17l-5-5',
  close: 'M18 6 6 18M6 6l12 12',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14m10 17-4.3-4.3',
  bell: 'M10.3 21a1.94 1.94 0 0 0 3.4 0M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9',
  projects: 'M4 20a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2zM8 13h8',
  applications:
    'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  periods: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18m-9 6 2 2 4-4',
  ingestion: 'M12 13v8m-4-4 4-4 4 4M20.4 14.5A5 5 0 0 0 18 5a7 7 0 0 0-13.3 2A4.5 4.5 0 0 0 5 16h2',
  registers: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  reports: 'M3 3v18h18M7 18v-6M12 18V8M17 18V5',
  intelligence:
    'M12 3a5 5 0 0 1 5 5c0 1.9-1 2.9-1.8 4-.6.8-.9 1.5-.9 2.4H9.7c0-.9-.3-1.6-.9-2.4C8 10.9 7 9.9 7 8a5 5 0 0 1 5-5M10 18h4M10.5 21h3',
  lifecycle: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  sun: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8',
  density: 'M3 4h18v7H3zM3 13h18v7H3z',
  lock: 'M4 11h16v10H4zM8 11V7a4 4 0 0 1 8 0v4',
  seal: 'M12 2l2.2 1.6 2.7-.3 1.1 2.5 2.4 1.3-.6 2.6.6 2.6-2.4 1.3-1.1 2.5-2.7-.3L12 19.6 9.8 18l-2.7.3-1.1-2.5L3.6 14.5l.6-2.6-.6-2.6L6 8l1.1-2.5 2.7.3zm-3 10 2 2 4-4',
  filter: 'M3 5h18l-7 8v7l-4-2v-5z',
  export: 'M12 3v12m-5-5 5 5 5-5M5 21h14',
  open: 'M7 17 17 7M8 7h9v9',
  pin: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7v5l3.5 2',
  alert: 'M12 3 2 20h20zM12 9v5M12 17h.01',
  warning: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 8v4M12 16h.01',
  money: 'M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  chart: 'M3 3v18h18M7 15l4-5 3 3 5-6',
  gate: 'M4 20h16M6 20V9l6-5 6 5v11M10 20v-6h4v6',
  map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14',
  image: 'M3 5h18v14H3zM3 15l5-5 4 4 3-3 6 6',
  note: 'M4 4h16v16H4zM8 9h8M8 13h5',
  users: 'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 20v-2a4 4 0 0 0-3-3.87M16 2.13a4 4 0 0 1 0 7.75',
  refresh: 'M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5',
} as const

export type IconName = keyof typeof PATHS

export function Icon({
  name,
  size = 16,
  className,
  strokeWidth = 1.7,
}: {
  name: IconName
  size?: number
  className?: string
  strokeWidth?: number
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      data-icon={name}
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}

/** The copilot spark, which is filled rather than stroked and so does not fit the set. */
export function Spark({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
      <path d="M19 15.2l.7 1.7 1.8.7-1.8.7-.7 1.7-.7-1.7-1.8-.7 1.8-.7z" />
    </svg>
  )
}
